import { ParsedUrlQuery } from 'querystring';

import { SEARCH_BY_COUNTY_SUFFIX } from '../consts';
import { enumToArray } from './enum.utils';
import { canonizeFlatType } from './flatType.utils';
import {
  extractCountyCityAndZoneFromQuery,
  extractFlatTypeFromQuery,
  slugifySearchQuery,
} from './searchQuery.utils';
import { SearchFilter } from '../hooks/useSearchService';
import { IFlat } from '../model/flat.model';
import { FlatOrderBy } from '../model/enums/flatOrderBy.enum';
import { FlatType } from '../model/enums/flatType.enum';

export const sortResults = (resultsToSort: IFlat[], orderBy: FlatOrderBy) => {
  if (orderBy === FlatOrderBy.PRICE_ASC) {
    resultsToSort.sort((a, b) => a.price - b.price);
  } else {
    resultsToSort.sort((a, b) => b.price - a.price);
  }
};

export const computeResults = (
  flats: IFlat[],
  filter: SearchFilter,
  orderBy = FlatOrderBy.PRICE_ASC
): IFlat[] => {
  let computedResults = [...flats];

  if (Array.isArray(filter.types)) {
    computedResults = computedResults.filter((flat) =>
      filter.types.some((type) => canonizeFlatType(flat.type) === type)
    );
  }
  if (typeof filter.priceMin === 'number') {
    computedResults = computedResults.filter(
      (flat) => flat.price >= filter.priceMin
    );
  }
  if (typeof filter.priceMax === 'number') {
    computedResults = computedResults.filter(
      (flat) => flat.price <= filter.priceMax
    );
  }
  if (typeof filter.sqrMetersMin === 'number') {
    computedResults = computedResults.filter(
      (flat) => flat.sqrMeters >= filter.sqrMetersMin
    );
  }
  if (typeof filter.sqrMetersMax === 'number') {
    computedResults = computedResults.filter(
      (flat) => flat.sqrMeters <= filter.sqrMetersMax
    );
  }
  if (Array.isArray(filter.rooms)) {
    computedResults = computedResults.filter((flat) => {
      if (
        typeof filter.roomsMin === 'number' &&
        flat.rooms >= filter.roomsMin
      ) {
        return true;
      }
      return filter.rooms.some((r) => flat.rooms === r);
    });
  } else if (typeof filter.roomsMin === 'number') {
    computedResults = computedResults.filter(
      (flat) => flat.rooms >= filter.roomsMin
    );
  }
  if (Array.isArray(filter.bathrooms)) {
    computedResults = computedResults.filter((flat) => {
      if (
        typeof filter.bathroomsMin === 'number' &&
        flat.bathrooms >= filter.bathroomsMin
      ) {
        return true;
      }
      return filter.bathrooms.some((r) => flat.bathrooms === r);
    });
  } else if (typeof filter.bathroomsMin === 'number') {
    computedResults = computedResults.filter(
      (flat) => flat.bathrooms >= filter.bathroomsMin
    );
  }
  if (Array.isArray(filter.characteristics)) {
    computedResults = computedResults.filter((flat) =>
      filter.characteristics.every((characteristic) => {
        return !!flat[
          `has${characteristic
            .charAt(0)
            .toUpperCase()}${characteristic.substring(1)}`
        ];
      })
    );
  }

  if (filter.mapBounds) {
    if (typeof google !== 'undefined' && typeof google.maps !== 'undefined') {
      computedResults = computedResults.filter((flat) => {
        const point = new google.maps.LatLng(
          flat.approximateLatitude,
          flat.approximateLongitude
        );
        return new google.maps.LatLngBounds(filter.mapBounds).contains(point);
      });
    }
  } else if (filter.mapArea) {
    computedResults = computedResults.filter((flat) =>
      flat.mapAreaIds.includes(filter.mapArea.id)
    );
  } else {
    if (filter.zone) {
      computedResults = computedResults.filter(
        (flat) => slugifySearchQuery(flat.zone) === filter.zone
      );
    }
    if (filter.city) {
      computedResults = computedResults.filter(
        (flat) => slugifySearchQuery(flat.city) === filter.city
      );
    }
    if (filter.county) {
      computedResults = computedResults.filter(
        (flat) =>
          slugifySearchQuery(flat.county) + SEARCH_BY_COUNTY_SUFFIX ===
          filter.county
      );
    }
  }

  if (filter.q) {
    const qLower = filter.q.toLowerCase();
    computedResults = computedResults.filter(
      (flat) =>
        flat.city.toLowerCase().includes(qLower) ||
        flat.zone.toLowerCase().includes(qLower) ||
        flat.address.toLowerCase().includes(qLower) ||
        flat.titleWeb?.toLowerCase().includes(qLower)
    );
  }

  sortResults(computedResults, orderBy);

  return computedResults;
};

export const generateFilterFromQuery = (
  query: ParsedUrlQuery
): SearchFilter => {
  const filter: SearchFilter = {};

  if (query.priceMin) {
    filter.priceMin = +(query.priceMin as string);
  }
  if (query.priceMax) {
    filter.priceMax = +(query.priceMax as string);
  }
  if (query.sqrMetersMin) {
    filter.sqrMetersMin = +(query.sqrMetersMin as string);
  }
  if (query.sqrMetersMax) {
    filter.sqrMetersMax = +(query.sqrMetersMax as string);
  }
  if (query.rooms) {
    filter.rooms = Array.isArray(query.rooms)
      ? query.rooms.map((r) => +r)
      : [+query.rooms];
  }
  if (query.roomsMin) {
    filter.roomsMin = +(query.roomsMin as string);
  }
  if (query.bathrooms) {
    filter.bathrooms = Array.isArray(query.bathrooms)
      ? query.bathrooms.map((r) => +r)
      : [+query.bathrooms];
  }
  if (query.bathroomsMin) {
    filter.bathroomsMin = +(query.bathroomsMin as string);
  }
  if (query.characteristics) {
    filter.characteristics = Array.isArray(query.characteristics)
      ? query.characteristics
      : [query.characteristics];
  }

  let county = query.county as string;
  let city = query.city as string;
  let zone = query.zone as string;
  if (!county && !city && !zone && query.query) {
    const countyCityZone = extractCountyCityAndZoneFromQuery(
      query.query as string
    );
    county = countyCityZone[0];
    city = countyCityZone[1];
    zone = countyCityZone[2];
  }

  let q = query.q as string;
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    q = searchParams.get('q') || q;
    q = q && decodeURI(q);
  }

  let types: string[] | undefined;
  if (query.types) {
    types = Array.isArray(query.types) ? query.types : [query.types];
  }
  let flatType = query.flatType
    ? extractFlatTypeFromQuery(query.flatType as string)
    : undefined;
  if (typeof window !== 'undefined' && window.location.href.includes('pisos')) {
    flatType = FlatType.PISO;
  }
  if (!types && flatType) {
    types = [flatType];
  }

  return {
    ...filter,
    county,
    city,
    zone,
    q,
    types,
  };
};

export const generateQueryFromFilter = (
  filter: SearchFilter
): ParsedUrlQuery => {
  const query: ParsedUrlQuery = {};
  if (filter.county) {
    query.county = filter.county;
  }
  if (filter.city) {
    query.city = filter.city;
  }
  if (filter.zone) {
    query.zone = filter.zone;
  }
  if (filter.q) {
    query.q = filter.q;
  }
  if (Array.isArray(filter.types) && filter.types.length) {
    query.types = filter.types;
  } else if (filter.types === null) {
    query.types = enumToArray(FlatType, 'string', 'label', 'value').map(
      (t) => t.value as string
    );
  }
  if (typeof filter.priceMin === 'number') {
    query.priceMin = filter.priceMin + '';
  }
  if (typeof filter.priceMax === 'number') {
    query.priceMax = filter.priceMax + '';
  }
  if (typeof filter.sqrMetersMin === 'number') {
    query.sqrMetersMin = filter.sqrMetersMin + '';
  }
  if (typeof filter.sqrMetersMax === 'number') {
    query.sqrMetersMax = filter.sqrMetersMax + '';
  }
  if (Array.isArray(filter.rooms) && filter.rooms.length) {
    query.rooms = filter.rooms.map((r) => r + '');
  }
  if (typeof filter.roomsMin === 'number') {
    query.roomsMin = filter.roomsMin + '';
  }
  if (Array.isArray(filter.bathrooms) && filter.bathrooms.length) {
    query.bathrooms = filter.bathrooms.map((r) => r + '');
  }
  if (typeof filter.bathroomsMin === 'number') {
    query.bathroomsMin = filter.bathroomsMin + '';
  }
  if (Array.isArray(filter.characteristics) && filter.characteristics.length) {
    query.characteristics = filter.characteristics;
  }
  return query;
};
